/**
 * Lay Deck — frontend counter widget + keyboard navigation.
 *
 * Bundled into frontend.app.min.js. The widget lives in <body> (outside the
 * AJAX-swap content zone) so it persists across slide transitions; we recompute
 * its state from `layData.deckMeta` (a flat list of all deck slides emitted by
 * Frontend::get_deck_meta()) after every Lay AJAX navigation.
 *
 * Why this design: wp_footer-rendered <script> blocks are NOT swapped by Lay's
 * AJAX nav — only the main content area is. So we can't rely on a per-page
 * inline data block; instead we use the once-localized `layData.deckMeta` and
 * key off `body[data-id]`, which Lay updates on every navigation.
 *
 * Navigation reuses Lay's global click-delegate on `a[data-type]` (see
 * frontend/history/bind_anchors.js) — clicking the rendered anchor dispatches
 * pushState + the configured Navigation Animations.
 *
 * Preloading: Lay's preload_controller hover-binds globally on
 * `a[data-id][data-type]`. After each render we synthesize a `mouseenter`
 * on the next anchor so the layout cache prefetches the upcoming slide.
 * (No-op when caching is disabled — preload_controller bails internally.)
 *
 * Auto-hide: on hover-capable (i.e. desktop) devices the widget fades out
 * after IDLE_MS of inactivity and fades back in on mouseenter. Touch-only
 * devices (`(hover: none)` or no fine pointer) keep it always-visible since
 * there's no hover concept to bring it back.
 */
var IDLE_MS = 4000

var deck_controller = {
	i: function () {
		init()
	},
}

var widget = null
var els = {}
var deckMeta = null
var idleTimer = null
var supportsHoverFade = false
var isHovered = false
// When true, the next update() (i.e. the one after AJAX nav) won't pop the
// widget back into view or reset the auto-hide timer. Set by onKey so that
// pure keyboard-only navigation keeps the widget faded — only mouseenter
// brings it back. Mouse clicks on the widget buttons leave the flag clear,
// so they re-show as before.
var skipShowOnNextUpdate = false

function getDeckMeta() {
	if (deckMeta !== null) return deckMeta
	if (typeof layData === 'undefined' || typeof layData.deckMeta === 'undefined') return null
	try {
		deckMeta = JSON.parse(layData.deckMeta)
	} catch (e) {
		deckMeta = null
	}
	return deckMeta
}

function buildWidget() {
	if (widget) return widget
	widget = document.createElement('div')
	widget.id = 'lay-deck-counter'
	widget.className = 'lay-deck-counter is-hidden'
	widget.setAttribute('aria-label', 'Deck navigation')
	// Same chevron-left SVG for both buttons; the right one is mirrored via
	// CSS (transform: scaleX(-1) on .lay-deck-counter__next svg).
	var chevronSvg =
		'<svg width="24" height="24" fill="none" viewBox="0 0 24 24" aria-hidden="true">' +
			'<path fill="currentColor" fill-rule="evenodd" d="M13.854 7.146a.5.5 0 0 1 0 .708L9.707 12l4.147 4.146a.5.5 0 0 1-.708.708l-4.5-4.5a.5.5 0 0 1 0-.708l4.5-4.5a.5.5 0 0 1 .708 0" clip-rule="evenodd"></path>' +
		'</svg>'
	widget.innerHTML =
		'<a class="lay-deck-counter__btn lay-deck-counter__prev" aria-label="Previous slide">' + chevronSvg + '</a>' +
		'<span class="lay-deck-counter__sep" aria-hidden="true"></span>' +
		'<span class="lay-deck-counter__num" aria-live="polite">— of —</span>' +
		'<span class="lay-deck-counter__sep" aria-hidden="true"></span>' +
		'<a class="lay-deck-counter__btn lay-deck-counter__next" aria-label="Next slide">' + chevronSvg + '</a>'
	document.body.appendChild(widget)
	els.prev = widget.querySelector('.lay-deck-counter__prev')
	els.next = widget.querySelector('.lay-deck-counter__next')
	els.num = widget.querySelector('.lay-deck-counter__num')

	// While the cursor is over the widget we cancel the hide timer entirely —
	// the widget shouldn't fade while the user is reaching for it. On leave
	// we re-arm the timer so it eventually fades when they move away.
	widget.addEventListener('mouseenter', function () {
		if (!supportsHoverFade) return
		isHovered = true
		widget.classList.remove('is-faded')
		clearHideTimer()
	})
	widget.addEventListener('mouseleave', function () {
		if (!supportsHoverFade) return
		isHovered = false
		armHideTimer()
	})
	return widget
}

function destroyWidget() {
	if (!widget) return
	clearHideTimer()
	if (widget.parentNode) {
		widget.parentNode.removeChild(widget)
	}
	widget = null
	els = {}
	isHovered = false
}

function clearHideTimer() {
	if (idleTimer) {
		clearTimeout(idleTimer)
		idleTimer = null
	}
}

function armHideTimer() {
	clearHideTimer()
	if (!supportsHoverFade) return
	if (isHovered) return
	idleTimer = setTimeout(function () {
		if (widget) widget.classList.add('is-faded')
	}, IDLE_MS)
}

function showWidget() {
	if (!widget) return
	widget.classList.remove('is-faded')
	armHideTimer()
}

function applyAnchor(el, slide) {
	if (slide && slide.url) {
		el.setAttribute('href', slide.url)
		el.setAttribute('data-type', 'lay_deck')
		el.setAttribute('data-id', String(slide.id))
		el.setAttribute('data-title', slide.title || '')
		el.classList.remove('is-disabled')
		el.removeAttribute('aria-disabled')
	} else {
		el.removeAttribute('href')
		el.removeAttribute('data-type')
		el.removeAttribute('data-id')
		el.removeAttribute('data-title')
		el.classList.add('is-disabled')
		el.setAttribute('aria-disabled', 'true')
	}
}

function update() {
	var meta = getDeckMeta()
	var bodyType = document.body.getAttribute('data-type')
	var bodyId = document.body.getAttribute('data-id')

	// Only show on a deck slide page (any data-type referencing the lay_deck CPT).
	var onDeck = bodyType === 'lay_deck' || bodyType === 'deck'
	if (!onDeck || !meta || !meta.length || !bodyId) {
		destroyWidget()
		return
	}

	// Find current slide by id (deckMeta entries have integer ids; data-id is a string).
	var idx = -1
	for (var i = 0; i < meta.length; i++) {
		if (String(meta[i].id) === String(bodyId)) {
			idx = i
			break
		}
	}
	if (idx === -1) {
		destroyWidget()
		return
	}

	buildWidget()
	widget.classList.remove('is-hidden')
	els.num.textContent = (idx + 1) + ' of ' + meta.length
	applyAnchor(els.prev, idx > 0 ? meta[idx - 1] : null)
	applyAnchor(els.next, idx < meta.length - 1 ? meta[idx + 1] : null)
	if (skipShowOnNextUpdate) {
		// Keyboard nav — keep the existing fade state untouched. The user will
		// see the new slide info next time they hover the widget area.
		skipShowOnNextUpdate = false
	} else {
		// Each render restarts the auto-hide timer (so a fresh nav un-fades the widget).
		showWidget()
	}

	if (idx < meta.length - 1) {
		try {
			els.next.dispatchEvent(new Event('mouseenter', { bubbles: true }))
		} catch (e) {
			/* old browsers — preload via real hover only */
		}
	}
}

function isTextInputFocused() {
	var a = document.activeElement
	if (!a) return false
	var tag = (a.tagName || '').toLowerCase()
	if (tag === 'input' || tag === 'textarea' || tag === 'select') return true
	if (a.isContentEditable) return true
	return false
}

function clickIfReal(el) {
	if (!el || el.classList.contains('is-disabled') || !el.getAttribute('href')) return
	el.click()
}

function onKey(e) {
	if (e.metaKey || e.ctrlKey || e.altKey) return
	if (isTextInputFocused()) return
	if (e.key === 'ArrowLeft') {
		if (supportsHoverFade) skipShowOnNextUpdate = true
		clickIfReal(els.prev)
		e.preventDefault()
	}
	if (e.key === 'ArrowRight') {
		if (supportsHoverFade) skipShowOnNextUpdate = true
		clickIfReal(els.next)
		e.preventDefault()
	}
}

function init() {
	// `(hover: none)` matches touch-only devices (phones/tablets without a
	// fine pointer). On those we keep the widget always visible because there's
	// no hover gesture to bring it back from a faded-out state.
	if (window.matchMedia) {
		var mq = window.matchMedia('(hover: hover) and (pointer: fine)')
		supportsHoverFade = !!mq.matches
		if (typeof mq.addEventListener === 'function') {
			mq.addEventListener('change', function (e) {
				supportsHoverFade = !!e.matches
				if (!supportsHoverFade) {
					if (widget) widget.classList.remove('is-faded')
					if (idleTimer) { clearTimeout(idleTimer); idleTimer = null }
				} else {
					showWidget()
				}
			})
		}
	}
	update()
	document.addEventListener('keydown', onKey)
	if (window.laytheme && typeof window.laytheme.on === 'function') {
		window.laytheme.on('newpageshown', function () {
			update()
		})
	}
}

export default deck_controller
